import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import SiretVerifier from "@/components/SiretVerifier";
import SEO from "@/components/SEO";
import { getSEO } from "@/lib/seo-config";
import { HardHat, User as UserIcon, ShieldCheck, Briefcase, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, slugify } from "@/lib/categories";
import { apiClient } from "@/lib/api";

const SPECIALTIES = CATEGORIES.filter((c) => c !== "Autre");

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "artisan" ? "artisan" : "demandeur";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    company: "",
    city: "",
    bio: "",
  });
  const [specs, setSpecs] = useState([]);
  const [siretInfo, setSiretInfo] = useState(null); // {valid, denomination, naf_label, active, siret}
  const [demandeurType, setDemandeurType] = useState("particulier");
  const [demandeurTypes, setDemandeurTypes] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    apiClient.get("/demandeur-types").then(({ data }) => setDemandeurTypes(data.types || [])).catch(() => {});
  }, []);

  const toggleSpec = (s) => {
    setSpecs((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const onSiretVerified = (d) => {
    setSiretInfo(d);
    setForm((f) => ({
      ...f,
      // Auto-fill company with official denomination if not typed yet
      company: f.company || d?.denomination || "",
      // Auto-fill city with siège commune if not typed yet
      city: f.city || d?.city || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error("Vous devez accepter les CGU, CGV et les Mentions légales.");
      return;
    }
    setBusy(true);
    const payload = { ...form, role, specialties: role === "artisan" ? specs : [], terms_accepted: true };
    if (role === "demandeur") {
      payload.demandeur_type = demandeurType;
    }
    if (role === "artisan" && siretInfo?.valid && siretInfo?.siret) {
      payload.siret = siretInfo.siret;
      payload.siret_data = siretInfo;
    }
    const r = await register(payload);
    setBusy(false);
    if (r.ok) {
      toast.success(siretInfo?.valid ? "Compte créé · SIRET vérifié !" : "Compte créé !");
      const next = new URLSearchParams(window.location.search).get("next");
      nav(next || "/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  const accentClass = (active) =>
    active
      ? "bg-[#FF4D00] text-white border-[#FF4D00]"
      : "bg-white text-[#0A0A0B] border-[#0A0A0B] hover:bg-zinc-100";

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <SEO cfg={getSEO("/register")} />
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="border-2 border-[#0A0A0B] bg-white p-8 lg:p-10">
          <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">// Inscription</div>
          <h1 className="display text-3xl lg:text-4xl mt-2 leading-tight">
            Créer mon compte Batisur · <span className="text-[#FF4D00]">Particulier ou artisan BTP</span>
          </h1>
          <p className="text-sm text-zinc-600 mt-3">
            Inscription 100% gratuite. Aucune CB demandée. 30 jours Pack Or offerts aux artisans à l'inscription.
          </p>

          <h2 className="sr-only">Choisissez votre rôle : demandeur de travaux ou artisan du bâtiment</h2>
          <div className="grid grid-cols-2 gap-3 mt-8">
            <button
              type="button"
              onClick={() => setRole("demandeur")}
              className={`border-2 p-5 text-left lift ${accentClass(role === "demandeur")}`}
              data-testid="role-demandeur"
            >
              <UserIcon className="w-6 h-6" />
              <div className="display text-2xl mt-3">Demandeur</div>
              <div className="text-xs mt-1 opacity-80">Je veux faire réaliser des travaux</div>
            </button>
            <button
              type="button"
              onClick={() => setRole("artisan")}
              className={`border-2 p-5 text-left lift ${accentClass(role === "artisan")}`}
              data-testid="role-artisan"
            >
              <HardHat className="w-6 h-6" />
              <div className="display text-2xl mt-3">Artisan</div>
              <div className="text-xs mt-1 opacity-80">Je suis professionnel du bâtiment</div>
            </button>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
            {/* Step indicator */}
            <div className="flex items-center gap-3 mb-2">
              <div className={`flex items-center gap-2 mono text-[10px] uppercase tracking-widest ${step === 1 ? "text-[#FF4D00]" : "text-emerald-700"}`}>
                <div className={`w-6 h-6 border-2 flex items-center justify-center font-bold text-xs ${step === 1 ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-emerald-500 border-emerald-500 text-white"}`}>
                  {step === 1 ? "1" : "✓"}
                </div>
                Essentiel (30s)
              </div>
              <div className={`flex-1 h-0.5 ${step === 2 ? "bg-[#FF4D00]" : "bg-zinc-300"}`} />
              <div className={`flex items-center gap-2 mono text-[10px] uppercase tracking-widest ${step === 2 ? "text-[#FF4D00]" : "text-zinc-400"}`}>
                <div className={`w-6 h-6 border-2 flex items-center justify-center font-bold text-xs ${step === 2 ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-white border-zinc-300 text-zinc-400"}`}>
                  2
                </div>
                Détails
              </div>
            </div>

            {/* STEP 1 : 4 champs essentiels */}
            {step === 1 && (
              <>
                <Field
                  label={role === "artisan" ? "Nom du référent" : "Nom complet"}
                  testid="reg-name"
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  required
                />
                <Field
                  label="Email"
                  type="email"
                  testid="reg-email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  required
                />
                <Field
                  label="Mot de passe (min. 6 caractères)"
                  type="password"
                  testid="reg-password"
                  value={form.password}
                  onChange={(v) => setForm({ ...form, password: v })}
                  required
                />
                <Field
                  label="Téléphone (optionnel)"
                  testid="reg-phone"
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!form.name || !form.email || !form.password) {
                      toast.error("Nom, email et mot de passe requis");
                      return;
                    }
                    if (form.password.length < 6) {
                      toast.error("Mot de passe : 6 caractères minimum");
                      return;
                    }
                    setStep(2);
                  }}
                  className="btn-batisur w-full flex items-center justify-center gap-2"
                  data-testid="reg-next-step"
                >
                  Continuer <ArrowRight className="w-4 h-4" />
                </button>
                <div className="text-center mono text-[10px] uppercase tracking-widest text-zinc-500">
                  4 champs · Étape suivante = plus de personnalisation
                </div>
              </>
            )}

            {/* STEP 2 : détails métier & profil */}
            {step === 2 && (
              <>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF4D00] inline-flex items-center gap-1"
                  data-testid="reg-back-step"
                >
                  ← Retour étape 1
                </button>

                <div className="grid grid-cols-2 gap-4">
              <Field
                label="Ville"
                testid="reg-city"
                value={form.city}
                onChange={(v) => setForm({ ...form, city: v })}
              />
              {role === "artisan" && (
                <Field
                  label="Société / Raison sociale"
                  testid="reg-company"
                  value={form.company}
                  onChange={(v) => setForm({ ...form, company: v })}
                />
              )}
              {role === "demandeur" && (
                <Field
                  label="Société (optionnel pour les pros)"
                  testid="reg-company-demandeur"
                  value={form.company}
                  onChange={(v) => setForm({ ...form, company: v })}
                />
              )}
            </div>

            {role === "demandeur" && demandeurTypes.length > 0 && (
              <div>
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600 flex items-center gap-2">
                  <Briefcase className="w-3.5 h-3.5 text-[#FF4D00]" /> Vous êtes
                </label>
                <select
                  value={demandeurType}
                  onChange={(e) => setDemandeurType(e.target.value)}
                  className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
                  data-testid="reg-demandeur-type"
                >
                  {demandeurTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  Cela nous permet d'adapter votre tableau de bord et les statistiques admin.
                </div>
              </div>
            )}

            {role === "artisan" && (
              <>
                <div className="border-2 border-dashed border-[#FF4D00] bg-orange-50 p-1">
                  <SiretVerifier captureOnly onVerified={onSiretVerified} />
                </div>
                <div className="text-xs text-zinc-600 -mt-2 flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Optionnel à l'inscription, fortement recommandé.</strong> Un SIRET vérifié vous débloque le badge officiel,
                    les devis ≥ 5000€ et l'éligibilité au Pack Or. Vous pourrez l'ajouter plus tard depuis votre profil.
                  </span>
                </div>

                <div>
                  <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">
                    Spécialités ({specs.length})
                  </label>
                  <div className="flex flex-wrap gap-1.5 mt-2 max-h-48 overflow-auto scrollbar-thin border-2 border-zinc-200 p-3">
                    {SPECIALTIES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleSpec(s)}
                        className={`px-2.5 py-1 text-[11px] border-2 mono uppercase tracking-wider transition ${
                          specs.includes(s)
                            ? "bg-[#0A0A0B] text-white border-[#0A0A0B]"
                            : "bg-white text-[#0A0A0B] border-zinc-300 hover:border-[#0A0A0B]"
                        }`}
                        data-testid={`spec-${slugify(s)}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">
                    Présentation
                  </label>
                  <textarea
                    rows="3"
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D00] bg-white"
                    placeholder="Présentez votre expertise, années d'expérience, etc."
                    data-testid="reg-bio"
                  />
                </div>
              </>
            )}

                <label
                  className={`flex items-start gap-3 border-2 p-4 cursor-pointer transition ${
                    termsAccepted ? "border-[#FF4D00] bg-orange-50" : "border-zinc-300 hover:border-[#0A0A0B] bg-white"
                  }`}
                  data-testid="terms-checkbox-wrapper"
                >
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#FF4D00] flex-shrink-0"
                    data-testid="terms-checkbox"
                  />
                  <span className="text-xs leading-relaxed text-zinc-700">
                    J'ai lu et j'accepte les{" "}
                    <Link to="/cgu" target="_blank" rel="noopener noreferrer" className="text-[#FF4D00] font-semibold hover:underline" data-testid="terms-cgu-link">
                      Conditions Générales d'Utilisation
                    </Link>
                    , les{" "}
                    <Link to="/cgv" target="_blank" rel="noopener noreferrer" className="text-[#FF4D00] font-semibold hover:underline" data-testid="terms-cgv-link">
                      Conditions Générales de Vente
                    </Link>{" "}
                    et les{" "}
                    <Link to="/mentions-legales" target="_blank" rel="noopener noreferrer" className="text-[#FF4D00] font-semibold hover:underline" data-testid="terms-mentions-link">
                      Mentions légales
                    </Link>{" "}
                    de Batisur Connect (SAS, 29 rue Tronchet, 75008 Paris). Mon acceptation est horodatée et opposable.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={busy || !termsAccepted}
                  className="btn-batisur w-full disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="register-submit"
                >
                  {busy ? "Création…" : `Créer mon compte ${role}`}
                </button>
              </>
            )}
          </form>

          <div className="mt-8 pt-6 border-t-2 border-zinc-200 text-sm">
            Déjà inscrit ?{" "}
            <Link to="/login" className="text-[#FF4D00] font-semibold hover:underline" data-testid="register-to-login">
              Se connecter →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D00] bg-white"
        data-testid={testid}
      />
    </div>
  );
}
